import type {
    DayAvailability,
    ServiceId,
    SessionId,
    SlotAvailability
} from '@/types'

import { weekdayOf } from '@/lib/dates'

import {
    practiceDays,
    serviceOrder,
    sessionOrder,
    sessionQuota
} from '@/constants/clinic'

/** Booked requests per slot, keyed by countKey() */
export type BookingCounts = Record<string, number>

export const countKey = (date: string, service: ServiceId, session: SessionId): string => {
    return `${date}|${service}|${session}`
}

/** FNV-1a hash mapped to [0, 1), stable across server and browser */
const hashToUnit = (input: string): number => {
    let hash = 0x811c9dc5

    for (let index = 0; index < input.length; index++) {
        hash ^= input.charCodeAt(index)
        hash = Math.imul(hash, 0x01000193)
    }

    return (hash >>> 0) / 4294967296
}

const nearlyFullThreshold = (quota: number): number => {
    return Math.max(2, Math.ceil(quota * 0.2))
}

/**
 * Demo occupancy (the section is labelled "Data demo"), so the table shows
 * realistic variety. Real requests from the database are added on top.
 */
const demoBooked = (date: string, service: ServiceId, session: SessionId, quota: number): number => {
    const roll = hashToUnit(countKey(date, service, session))

    if (roll < 0.08) return quota

    if (roll < 0.3) {
        const spread = Math.max(1, Math.ceil(quota * 0.2))
        return quota - 1 - (Math.floor(roll * 1000) % spread)
    }

    return Math.floor(quota * (0.1 + roll * 0.45))
}

export const isPracticing = (date: string, service: ServiceId, session: SessionId): boolean => {
    const weekday = weekdayOf(date)

    if (weekday < 1 || weekday > 6) return false

    return practiceDays[service][session][weekday - 1]
}

export const resolveSlot = (date: string, service: ServiceId, session: SessionId, counts: BookingCounts): SlotAvailability => {
    const quota = sessionQuota[service][session]

    if (!isPracticing(date, service, session)) return { status: 'tidak-praktik', remaining: 0, quota }

    const booked = demoBooked(date, service, session, quota) + (counts[countKey(date, service, session)] ?? 0)
    const remaining = Math.max(0, quota - booked)

    if (remaining === 0) return { status: 'penuh', remaining, quota }
    if (remaining <= nearlyFullThreshold(quota)) return { status: 'hampir-penuh', remaining, quota }

    return { status: 'tersedia', remaining, quota }
}

export const buildDayAvailability = (date: string, counts: BookingCounts): DayAvailability => {
    const slots = {} as DayAvailability['slots']

    for (const service of serviceOrder) {
        slots[service] = {} as Record<SessionId, SlotAvailability>

        for (const session of sessionOrder) {
            slots[service][session] = resolveSlot(date, service, session, counts)
        }
    }

    return {
        date,
        weekday: weekdayOf(date),
        slots
    }
}

export const buildAvailability = (dates: string[], counts: BookingCounts): DayAvailability[] => {
    return dates.map(date => buildDayAvailability(date, counts))
}

export const isBookable = (slot: SlotAvailability | undefined): boolean => {
    return slot?.status === 'tersedia' || slot?.status === 'hampir-penuh'
}
